import {
  Component,
  lazy,
  Suspense,
  useEffect,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { SessionProvider, useSession } from "./auth";
import { ThemeProvider } from "./theme";
import { Shell } from "./components/Shell";
import { EmptyState } from "./components/EmptyState";
import { SignIn } from "./screens/SignIn";
import { useLatestBuild } from "./useLatestBuild";
import { ProductConfigProvider, useProductConfig } from "./productConfig";
import { loadHome, loadGamePulse, loadGameSessions, loadGameRetention, loadGameFunnels, loadGameAudience, loadGameDiscovery, loadGameEconomy, loadGameProduct, loadGameProductDashboards, loadGameNotifications, loadGamePerformance, loadGameParity, loadGameDataHealth, loadGameExperiments, gameRouteLoader } from "./routeLoaders";
import { DashboardQueries } from "./DashboardQueries";
import { LoadingState } from "./components/QueryState";

/**
 * ONE SCREEN'S WORTH OF JAVASCRIPT, NOT TEN.
 *
 * Everything was in a single chunk: 1,028 KB minified, 302 KB gzipped, parsed
 * in full before the first pixel. A reader opening Sessions was downloading
 * and compiling Economy, Product, Parity and the funnel machinery to look at
 * one chart -- and paying it again on every hard refresh, which is exactly what
 * people do when they are waiting for a fix to appear.
 *
 * Route-level `lazy` splits it per screen. The shell, the router and the query
 * client stay in the entry chunk because every route needs them; each screen's
 * charts arrive when that screen does.
 *
 * Only the report is inside the screen's Suspense/error boundary. Game
 * navigation stays mounted while a report downloads, loads data or fails.
 */
const Home = lazy(async () => ({ default: (await loadHome()).Home }));
const GamePulse = lazy(async () => ({
  default: (await loadGamePulse()).GamePulse,
}));
const GameSessions = lazy(async () => ({
  default: (await loadGameSessions()).GameSessions,
}));
const GameRetention = lazy(async () => ({
  default: (await loadGameRetention()).GameRetention,
}));
const GameFunnels = lazy(async () => ({
  default: (await loadGameFunnels()).GameFunnels,
}));
const GameAudience = lazy(async () => ({
  default: (await loadGameAudience()).GameAudience,
}));
const GameDiscovery = lazy(async () => ({
  default: (await loadGameDiscovery()).GameDiscovery,
}));
const GameEconomy = lazy(async () => ({
  default: (await loadGameEconomy()).GameEconomy,
}));
const GameProduct = lazy(async () => ({
  default: (await loadGameProduct()).GameProduct,
}));
const GameProductDashboards = lazy(() => loadGameProductDashboards().then(m=>({default:m.GameProductDashboards})));
const GameNotifications = lazy(() => loadGameNotifications().then(m=>({default:m.GameNotifications})));
const GamePerformance = lazy(async () => ({
  default: (await loadGamePerformance()).GamePerformance,
}));
const GameParity = lazy(async () => ({
  default: (await loadGameParity()).GameParity,
}));
const GameDataHealth = lazy(async () => ({
  default: (await loadGameDataHealth()).GameDataHealth,
}));
const GameExperiments = lazy(async () => ({
  default: (await loadGameExperiments()).GameExperiments,
}));

/**
 * A hard-loaded game URL already tells us which screen will be needed. Start
 * its chunk (and Vite's shared dependencies) while `/v1/me` is in flight,
 * instead of waiting for identity and then beginning another network wave.
 */
if (
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/g/")
) {
  void gameRouteLoader(window.location.pathname)().catch((error: unknown) => {
    // Lazy will surface the same failure to the boundary below. Catching the
    // eager request here prevents a second, unhandled failure before React can
    // offer the reader a way out.
    console.warn("Route prefetch failed; rendering recovery instead.", error);
  });
}

class RouteErrorBoundary extends Component<
  { children: ReactNode; resetKey: string },
  { failed: boolean; resetKey: string | null }
> {
  state = { failed: false, resetKey: null as string | null };

  static getDerivedStateFromProps(props: { resetKey: string }, state: { resetKey: string | null }) {
    return props.resetKey === state.resetKey ? null : { failed: false, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(
      "A routed screen failed to load.",
      error,
      info.componentStack,
    );
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert">
        <EmptyState
          title="This screen did not load"
          body="A new version or a temporary network problem interrupted this screen. Reload to try again."
          actions={[
            {
              label: "Reload this page",
              onClick: () => window.location.reload(),
            },
          ]}
        />
      </div>
    );
  }
}

const GameLayout = lazy(async () => ({
  default: (await import("./components/GameLayout")).GameLayout,
}));

function Startup(): ReactNode {
  const [slow, setSlow] = useState(false);
  const { config } = useProductConfig();

  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 2_000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="startup" role="status" aria-live="polite">
      <img src={config.product.icon} alt="" />
      <div>
        <div className="startup__title">
          {slow ? "Starting analytics…" : "Connecting…"}
        </div>
        <p className="startup__body">
          {slow
            ? "The service is waking up. Your current page will open automatically."
            : "Checking your session and preparing the dashboard."}
        </p>
      </div>
    </div>
  );
}

function Routed(): ReactNode {
  const { status } = useSession();
  const { loadMetadata, metadataStatus } = useProductConfig();
  const { pathname } = useLocation();
  // Keeping the previous range visible is useful; keeping another game's
  // numbers visible under a newly selected game is not. Remount the query
  // observers at that boundary so placeholder data never crosses games.
  const gameKey = pathname.startsWith("/g/")
    ? (pathname.split("/")[2] ?? "")
    : "home";
  // Picks up a deploy on its own, next time the tab is in the background. A
  // fix nobody can see until they think to hard-refresh is a fix that did not
  // ship for them.
  useLatestBuild();

  useEffect(() => {
    if (status === "signed-in" && metadataStatus === "idle") {
      void loadMetadata();
    }
  }, [loadMetadata, metadataStatus, status]);

  if (status === "loading") {
    return (
      <Shell>
        <Startup />
      </Shell>
    );
  }
  if (status === "signed-out") return <SignIn />;

  const screen = (
      <RouteErrorBoundary resetKey={pathname}>
        <Suspense fallback={<LoadingState label="view" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/g/:gameId" element={<GamePulse />} />
            <Route path="/g/:gameId/sessions" element={<GameSessions />} />
            <Route path="/g/:gameId/retention" element={<GameRetention />} />
            <Route path="/g/:gameId/funnels" element={<GameFunnels />} />
            <Route path="/g/:gameId/audience" element={<GameAudience />} />
            <Route path="/g/:gameId/discovery" element={<GameDiscovery />} />
            <Route
              path="/g/:gameId/thumbnails"
              element={<GameDiscovery thumbnails />}
            />
            <Route path="/g/:gameId/economy" element={<GameEconomy />} />
            <Route path="/g/:gameId/currency" element={<GameEconomy />} />
            <Route path="/g/:gameId/product" element={<GameProduct />} />
            <Route path="/g/:gameId/dashboards" element={<GameProductDashboards />} />
            <Route path="/g/:gameId/notifications" element={<GameNotifications />} />
            <Route path="/g/:gameId/shop" element={<GameProduct />} />
            <Route path="/g/:gameId/catalogue" element={<GameProduct />} />
            <Route path="/g/:gameId/gameplay" element={<GameProduct />} />
            <Route
              path="/g/:gameId/performance"
              element={<GamePerformance />}
            />
            <Route path="/g/:gameId/parity" element={<GameParity />} />
            <Route path="/g/:gameId/data-health" element={<GameDataHealth />} />
            <Route
              path="/g/:gameId/experiments"
              element={<GameExperiments />}
            />
            <Route path="*" element={<Home />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
  );
  return (
    <Shell>
      {gameKey === "home" ? screen : (
        <RouteErrorBoundary resetKey={gameKey}>
          <Suspense fallback={<LoadingState label="view" />}>
            <GameLayout key={gameKey} gameId={decodeURIComponent(gameKey)}>
              {screen}
            </GameLayout>
          </Suspense>
        </RouteErrorBoundary>
      )}
    </Shell>
  );
}

export function App(): ReactNode {
  return (
    <ThemeProvider>
      <ProductConfigProvider>
        {/* Commit the selected tab immediately; the report's own boundary shows
            loading while its code arrives, without delaying the navigation. */}
        <BrowserRouter useTransitions={false}>
          <SessionProvider>
            <DashboardQueries>
              <Routed />
            </DashboardQueries>
          </SessionProvider>
        </BrowserRouter>
      </ProductConfigProvider>
    </ThemeProvider>
  );
}
